import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import * as Sentry from '@sentry/react';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';
import type { UnreceivedResolution } from '@threadheads/shared';

type ProcessOrderItemInput = RouterInputs['purchaseOrders']['processOrderItem'];
type ProcessOrderItemOutput = RouterOutputs['purchaseOrders']['processOrderItem'];
type ReEditOrderItemInput = RouterInputs['purchaseOrders']['reEditOrderItem'];
type ReEditOrderItemOutput = RouterOutputs['purchaseOrders']['reEditOrderItem'];
type AddSurpriseItemInput = RouterInputs['purchaseOrders']['addSurpriseItem'];

/**
 * Hook for purchase order receiving workflow
 * Handles processing items and re-editing received quantities
 * Uses backend tRPC mutations for proper stock management with audit trails
 */
export function useCheckOrders() {
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { profile } = useAuth();
  const utils = trpc.useUtils();

  // PROCESS ORDER ITEM (add_stock, edited, not_received)
  // Backend tRPC mutation handles BOTH updating the item AND adjusting inventory
  const processItemMutation = trpc.purchaseOrders.processOrderItem.useMutation({
    onSuccess: (_result: ProcessOrderItemOutput, variables: ProcessOrderItemInput) => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
        void utils.inventory.getProducts.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();

      // Only show toast for stock additions, not for not_received
      if (variables.action === 'add_stock' || variables.action === 'edited') {
        // Success - component will handle the toast after inventory is added
      }
    },
    onError: (error: unknown) => {
      // Toast removed — handleSubmit in ReceivingCard handles per-item error reporting.
      // Keep Sentry capture for observability.
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'processItem',
          location_id: selectedLocation?.id,
          organization_id: organization?.id
        }
      });
    }
  });

  // RE-EDIT ORDER ITEM (adjust already processed items)
  // Backend tRPC mutation handles BOTH updating the item AND adjusting inventory difference
  const reEditItemMutation = trpc.purchaseOrders.reEditOrderItem.useMutation({
    onSuccess: (result: ReEditOrderItemOutput) => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
        void utils.inventory.getProducts.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();

      const diff = result.inventoryDifference;
      if (diff !== 0) {
        toast({
          title: "Item Re-edited Successfully",
          description: `Inventory ${diff > 0 ? 'increased' : 'decreased'} by ${Math.abs(diff)} units`,
        });
      } else {
        toast({
          title: "Item Re-edited Successfully",
          description: "No inventory adjustment needed",
        });
      }
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'reEditItem',
          location_id: selectedLocation?.id,
          organization_id: organization?.id
        },
        level: errorMessage.includes('Cannot reduce stock') ? 'warning' : 'error'
      });

      toast({
        title: "Error Re-editing Item",
        description: errorMessage || "Failed to re-edit item. Please try again.",
        variant: "destructive",
      });
    }
  });

  // REVERT RECEIVED ITEMS (BLU-344)
  const revertItemsMutation = trpc.purchaseOrders.revertReceivedItems.useMutation({
    onSuccess: (_result, _variables) => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
        void utils.inventory.getProducts.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'revertItems',
          location_id: selectedLocation?.id,
          organization_id: organization?.id,
        },
      });

      toast({
        title: 'Error Reverting Items',
        description: errorMessage || 'Failed to revert received items. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // ADD SURPRISE ITEM (BLU-516) — a SKU the supplier shipped but wasn't on
  // the original PO, added at receiving time.
  const addSurpriseItemMutation = trpc.purchaseOrders.addSurpriseItem.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
      }
      // BLU-576: OrderDetail now reads exclusively from getOrder, so refresh it
      // too — otherwise a surprise item added on the detail page won't appear
      // until staleTime elapses (risking a duplicate re-add). Mirrors every
      // other receiving mutation in this hook.
      void utils.purchaseOrders.getOrder.invalidate();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'addSurpriseItem',
          location_id: selectedLocation?.id,
          organization_id: organization?.id,
        },
      });

      toast({
        title: 'Error Adding Item',
        description: errorMessage || 'Failed to add the unexpected item. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // UPDATE SHIPPED QUANTITIES (packing list data entry)
  const updateShippedQuantitiesMutation = trpc.purchaseOrders.updateShippedQuantities.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'updateShippedQuantities',
          location_id: selectedLocation?.id,
          organization_id: organization?.id,
        },
      });

      toast({
        title: 'Error Saving Shipped Quantities',
        description: errorMessage || 'Failed to save shipped quantities. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // SHIP A TRANSFER WITH A QUANTITY PER SKU
  // Unlike the status-only ship this replaces, the server records what actually
  // went on the truck and holds the remainder committed at the source until
  // someone confirms it.
  const shipTransferMutation = trpc.purchaseOrders.shipTransfer.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'shipTransfer',
          location_id: selectedLocation?.id,
          organization_id: organization?.id,
        },
      });

      toast({
        title: 'Error Shipping Transfer',
        description: errorMessage || 'Failed to ship the transfer. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // CONFIRM THE UNITS THAT NEVER SHIPPED
  // Releases the stock still held committed at the source for those units.
  const confirmUnshippedMutation = trpc.purchaseOrders.confirmUnshipped.useMutation({
    onSuccess: () => {
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'confirmUnshipped',
          location_id: selectedLocation?.id,
          organization_id: organization?.id,
        },
      });

      toast({
        title: 'Error Confirming Unshipped Units',
        description: errorMessage || 'Failed to confirm the unshipped units. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // MARK SHIPPED UNITS THAT NEVER ARRIVED AS RETURNED OR SCRAPPED
  // Settles the in-transit remainder of a stamped transfer line so the
  // transfer can be completed.
  const resolveUnreceivedMutation = trpc.purchaseOrders.resolveUnreceived.useMutation({
    onSuccess: () => {
      // The destination's incoming drops by the resolved units.
      if (selectedLocation) {
        void utils.purchaseOrders.getOrders.invalidate({ locationId: selectedLocation.id });
        void utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
      } else {
        void utils.purchaseOrders.getOrders.invalidate();
        void utils.inventory.getProducts.invalidate();
      }
      void utils.purchaseOrders.getOrder.invalidate();
      void utils.inventory.getIncomingBreakdown.invalidate();
    },
    onError: (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Sentry.captureException(error, {
        tags: {
          component: 'useCheckOrders',
          operation: 'resolveUnreceived',
          location_id: selectedLocation?.id,
          organization_id: organization?.id,
        },
      });

      toast({
        title: 'Error Marking Units',
        description: errorMessage || 'Failed to mark the units. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Wrapper functions that match the expected signature
  const processItem = async ({
    itemId,
    action,
    receivedQuantity,
    notes
  }: {
    itemId: ProcessOrderItemInput['itemId'];
    action: ProcessOrderItemInput['action'];
    receivedQuantity?: ProcessOrderItemInput['receivedQuantity'];
    notes?: ProcessOrderItemInput['notes'];
  }) => {
    if (!selectedLocation) throw new Error("No location selected");
    if (!organization) throw new Error("No organization selected");

    // Determine received quantity based on action
    const quantity = action === 'not_received' ? 0 : (receivedQuantity || 0);

    // Get user info for audit trail
    const processedBy = profile
      ? `${profile.first_name} ${profile.last_name}`.trim() || profile.email
      : 'Unknown User';

    await processItemMutation.mutateAsync({
      itemId,
      action,
      receivedQuantity: quantity,
      notes,
      processedBy
    });

    return { action, quantity };
  };

  const reEditItem = async ({
    itemId,
    newQuantity,
    notes
  }: {
    itemId: ReEditOrderItemInput['itemId'];
    newQuantity: ReEditOrderItemInput['newReceivedQuantity'];
    notes?: ReEditOrderItemInput['notes'];
  }) => {
    if (!selectedLocation) throw new Error("No location selected");
    if (!organization) throw new Error("No organization selected");

    // Get user info for audit trail
    const processedBy = profile
      ? `${profile.first_name} ${profile.last_name}`.trim() || profile.email
      : 'Unknown User';

    return await reEditItemMutation.mutateAsync({
      itemId,
      newReceivedQuantity: newQuantity,
      notes,
      processedBy
    });
  };

  const revertItem = async (orderId: string, itemId: string, locationId: string, notes?: string) => {
    return await revertItemsMutation.mutateAsync({
      orderId,
      locationId,
      itemIds: [itemId],
      notes,
    });
  };

  const revertAllItems = async (orderId: string, locationId: string, itemIds?: string[], notes?: string) => {
    return await revertItemsMutation.mutateAsync({
      orderId,
      locationId,
      itemIds,
      notes,
    });
  };

  const updateShippedQuantities = async (
    orderId: string,
    items: Array<{ itemId: string; shippedQuantity: number }>
  ) => {
    if (!selectedLocation) throw new Error('No location selected');
    if (!organization) throw new Error('No organization selected');

    return await updateShippedQuantitiesMutation.mutateAsync({ orderId, items });
  };

  const shipTransfer = async (
    orderId: string,
    items: Array<{ itemId: string; shippedQuantity: number }>
  ) => {
    if (!selectedLocation) throw new Error('No location selected');
    if (!organization) throw new Error('No organization selected');

    return await shipTransferMutation.mutateAsync({
      orderId,
      locationId: selectedLocation.id,
      items,
    });
  };

  const confirmUnshipped = async (orderId: string, itemIds?: string[]) => {
    if (!selectedLocation) throw new Error('No location selected');
    if (!organization) throw new Error('No organization selected');

    return await confirmUnshippedMutation.mutateAsync({
      orderId,
      locationId: selectedLocation.id,
      itemIds,
    });
  };

  const resolveUnreceived = async (
    orderId: string,
    itemIds: string[],
    resolution: UnreceivedResolution
  ) => {
    if (!selectedLocation) throw new Error('No location selected');
    if (!organization) throw new Error('No organization selected');

    return await resolveUnreceivedMutation.mutateAsync({
      orderId,
      locationId: selectedLocation.id,
      itemIds,
      resolution,
    });
  };

  const addSurpriseItem = async (
    poId: AddSurpriseItemInput['poId'],
    skuId: AddSurpriseItemInput['skuId'],
    quantity: AddSurpriseItemInput['quantity']
  ) => {
    return await addSurpriseItemMutation.mutateAsync({ poId, skuId, quantity });
  };

  return {
    processItem,
    reEditItem,
    revertItem,
    revertAllItems,
    updateShippedQuantities,
    shipTransfer,
    confirmUnshipped,
    resolveUnreceived,
    addSurpriseItem,
    isProcessing: processItemMutation.isPending,
    isReEditing: reEditItemMutation.isPending,
    isReverting: revertItemsMutation.isPending,
    isSavingShipped: updateShippedQuantitiesMutation.isPending,
    isShipping: shipTransferMutation.isPending,
    isConfirmingUnshipped: confirmUnshippedMutation.isPending,
    isResolvingUnreceived: resolveUnreceivedMutation.isPending,
    isAddingSurpriseItem: addSurpriseItemMutation.isPending,
  };
}
